import { Alert, Card, Flex, Space, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '@/atoms/Button';
import {
  AssessmentStepPager,
  type AssessmentFlowStep,
} from '@/components/assessment/AssessmentStepPager';
import {
  AreaChartIcon as AreaChartOutlined,
  DeploymentIcon as DeploymentUnitOutlined,
  OutputIcon as FileTextOutlined,
  FundIcon as FundOutlined,
  ThunderboltIcon as ThunderboltFilled,
} from '@/icons';
import type { TranscriptAnalysisReviewModel } from '@/utils/transcriptAnalysisReviewParser';

const { Text, Title } = Typography;

/** Props for the `TranscriptAnalysisReview` full-review panel. */
export interface TranscriptAnalysisReviewProps {
  markdown: string;
  model: TranscriptAnalysisReviewModel;
  clientLabel: string;
  approvalDisabled?: boolean;
  busyAction?: 'edit' | 'approve' | null;
  signalPreviewLimit?: number;
  currentFlowStep?: AssessmentFlowStep;
  onNavigateAssessmentStep?: (step: AssessmentFlowStep) => void;
  onDownload: (markdown: string, filename: string) => void;
  onEdit: () => void;
  onBack: () => void;
  onApprove: () => void;
}

const Markdown: React.FC<{ children: string }> = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
);

function splitListItems(markdown: string): string[] {
  const lines = markdown.split('\n');
  const items: string[] = [];
  let current: string[] = [];
  for (const line of lines) {
    if (/^(?:[-*+]|\d+\.)\s+/.test(line) && current.length > 0) {
      const block = current
        .join('\n')
        .replace(/\n+\s*-{3,}\s*$/, '')
        .trim();
      if (block) {
        items.push(block);
      }
      current = [line];
    } else {
      current.push(line);
    }
  }
  const last = current
    .join('\n')
    .replace(/\n+\s*-{3,}\s*$/, '')
    .trim();
  if (last) {
    items.push(last);
  }
  return items;
}

function confidenceColor(confidence: string): string | undefined {
  if (confidence === 'High') {
    return 'green';
  }
  if (confidence === 'Medium') {
    return 'orange';
  }
  return undefined;
}

/** Displays the completed transcript analysis output — themes, signals, narrative, and gaps — with download and approval actions. */
export const TranscriptAnalysisReview: React.FC<TranscriptAnalysisReviewProps> = ({
  markdown,
  model,
  clientLabel,
  approvalDisabled = false,
  busyAction = null,
  signalPreviewLimit = 5,
  currentFlowStep = '2-review',
  onNavigateAssessmentStep,
  onDownload,
  onEdit,
  onApprove,
}) => {
  const [showAllSignals, setShowAllSignals] = useState(false);
  const confidenceRank = { High: 0, Medium: 1, Low: 2, Unknown: 3 };
  const sortedSignals = model.signals.toSorted(
    (a, b) => (confidenceRank[a.confidence] ?? 3) - (confidenceRank[b.confidence] ?? 3),
  );
  const visibleSignals = showAllSignals
    ? sortedSignals
    : sortedSignals.slice(0, signalPreviewLimit);
  const maturityScore = model.narrative?.numericMaturityScore;

  return (
    <div className="transcript-analysis-review">
      {/* Header — sticky, mirrors Step 1 (InterviewGuideStep) layout */}
      <div className="interview-guide-review__header">
        <Flex wrap="wrap" gap="middle" align="flex-start" justify="space-between">
          <div className="interview-prep-page__hero-heading">
            <div className="interview-prep-page__step-badge" aria-hidden="true">
              2
            </div>
            <div className="interview-prep-page__hero-text">
              <Title level={2} className="interview-prep-page__hero-title">
                Transcript Analysis — Ready for Review
              </Title>
              <Text type="secondary" className="interview-prep-page__hero-subtitle">
                {model.filename} · {clientLabel}
                {model.signals.length > 0 ? ` · ${model.signals.length} signals` : ''}
                {maturityScore !== null && maturityScore !== undefined
                  ? ` · Maturity: ${maturityScore}/5`
                  : ''}
              </Text>
            </div>
          </div>
          {onNavigateAssessmentStep ? (
            <Space direction="vertical" align="end">
              <AssessmentStepPager
                current={currentFlowStep}
                disableNext={!model.hasCompleteContract}
                onStepChange={onNavigateAssessmentStep}
              />
            </Space>
          ) : null}
        </Flex>
      </div>

      {/* InfoBanner */}
      <Alert
        banner
        type="info"
        showIcon
        message="Review the cross-stakeholder analysis below. Click Approve Output to save this to the File Cabinet."
      />

      {model.isStructured ? null : (
        <Alert
          type="warning"
          showIcon
          message="This analysis could not be structured. The complete Markdown remains available below."
        />
      )}

      {/* ThemesSection — single card, themes separated by rules */}
      <section aria-labelledby="analysis-themes">
        <Title id="analysis-themes" level={3}>
          <DeploymentUnitOutlined style={{ color: '#52c41a', marginRight: 8 }} />
          Cross-Stakeholder Themes
        </Title>
        <Card>
          {model.themes.length === 0 ? (
            <Text type="secondary">No themes identified in this analysis.</Text>
          ) : (
            model.themes.map((theme, index) => (
              <React.Fragment key={theme.number}>
                {index > 0 ? <hr className="transcript-analysis-review__theme-rule" /> : null}
                <div className="transcript-analysis-review__theme">
                  <Title level={5}>{`Theme ${theme.number} — ${theme.name}`}</Title>
                  <Markdown>{theme.summaryMarkdown}</Markdown>
                  {theme.tags.length > 0 ? (
                    <Space wrap>
                      {theme.tags.map((tag) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </Space>
                  ) : null}
                </div>
              </React.Fragment>
            ))
          )}
        </Card>
      </section>

      {/* SignalsSection */}
      <section aria-labelledby="analysis-signals">
        <div className="transcript-analysis-review__signals-header">
          <Title id="analysis-signals" level={3} style={{ margin: 0 }}>
            <ThunderboltFilled style={{ color: '#faad14', marginRight: 8 }} />
            Opportunity Signals
          </Title>
          {model.signals.length > signalPreviewLimit ? (
            <Text type="secondary">
              Showing top {signalPreviewLimit} of {model.signals.length} —{' '}
              <button
                type="button"
                onClick={() => onDownload(markdown, model.filename)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'inherit',
                  textDecoration: 'underline',
                  font: 'inherit',
                }}
              >
                download .md for full list
              </button>
            </Text>
          ) : null}
        </div>
        <div className="transcript-analysis-review__signals">
          {visibleSignals.length === 0 ? (
            <Card>
              <Text type="secondary">No opportunity signals identified in this analysis.</Text>
            </Card>
          ) : null}
          {visibleSignals.map((signal) => (
            <Card
              key={signal.number}
              title={signal.label}
              extra={<Tag color={confidenceColor(signal.confidence)}>{signal.confidence}</Tag>}
            >
              <blockquote className="transcript-analysis-review__quote">
                <Markdown>{signal.whatWasSaidMarkdown}</Markdown>
              </blockquote>
            </Card>
          ))}
        </div>
        {model.signals.length > signalPreviewLimit ? (
          <Button onClick={() => setShowAllSignals((v) => !v)}>
            {showAllSignals ? 'Show fewer' : `View all ${model.signals.length} signals`}
          </Button>
        ) : null}
      </section>

      {/* GapsSection — one card per group, items within each card separated by rules */}
      {model.gapGroups.length ? (
        <section aria-labelledby="analysis-gaps">
          <Title id="analysis-gaps" level={3}>
            <FundOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
            {model.sectionDTitle ?? 'Gaps & Conflicting Signals'}
          </Title>
          {model.gapGroups.map((group) => {
            const items = splitListItems(group.markdown);
            return (
              <Card key={group.kind} title={group.title} style={{ marginBottom: '1rem' }}>
                {items.length === 0 ? (
                  <Text type="secondary">No recommendations available for this period.</Text>
                ) : (
                  items.map((item, index) => (
                    <React.Fragment key={item}>
                      {index > 0 ? <hr className="transcript-analysis-review__theme-rule" /> : null}
                      <Markdown>{item}</Markdown>
                    </React.Fragment>
                  ))
                )}
              </Card>
            );
          })}
        </section>
      ) : null}

      {/* NarrativeSection */}
      {model.narrative ? (
        <section aria-labelledby="analysis-narrative">
          <Title id="analysis-narrative" level={3}>
            <AreaChartOutlined style={{ color: '#722ed1', marginRight: 8 }} />
            Analyst Narrative
          </Title>
          <Card className="transcript-analysis-review__narrative">
            <Markdown>{model.narrative.markdown}</Markdown>
          </Card>
        </section>
      ) : null}

      {/* Fallback blocks — single card, blocks separated by rules (mirrors ThemesSection) */}
      {model.fallbackBlocks.length ? (
        <section aria-labelledby="analysis-fallback">
          <Title id="analysis-fallback" level={3}>
            <FileTextOutlined style={{ color: '#8c8c8c', marginRight: 8 }} />
            Additional source content
          </Title>
          <Card>
            {model.fallbackBlocks.map((block, index) => (
              <React.Fragment key={`${block.order}-${block.title}`}>
                {index > 0 ? <hr className="transcript-analysis-review__theme-rule" /> : null}
                <div className="transcript-analysis-review__theme">
                  <Markdown>{block.markdown}</Markdown>
                </div>
              </React.Fragment>
            ))}
          </Card>
        </section>
      ) : null}

      {/* Footer — sticky, mirrors Step 1 (InterviewGuideStep) layout */}
      <div className="interview-guide-review__footer">
        <Space wrap align="center">
          <Button onClick={() => onDownload(markdown, model.filename)}>Download .md</Button>
          <Button loading={busyAction === 'edit'} onClick={onEdit}>
            Edit
          </Button>
          <Button
            type="primary"
            loading={busyAction === 'approve'}
            disabled={approvalDisabled || !model.hasCompleteContract}
            onClick={onApprove}
          >
            Approve Output
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default TranscriptAnalysisReview;
